import { useQuery } from '@tanstack/react-query';

import { ClientNetworkError } from '@/lib/errors/client';

import type { TokenSecurity, TokenSecurityApiResponse } from '@/utils/0x/token-security.types';

interface Params {
  tokenAddress: string | undefined;
}

const fetchTokenSecurity = async (tokenAddress: string): Promise<TokenSecurity | undefined> => {
  const url = `/api/tokens/security?address=${tokenAddress}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    // No row upstream -> surface as undefined so the UI shows Pending.
    if (res.status === 404) return undefined;
    const errorData = await res.json().catch(() => ({}));
    throw new ClientNetworkError(errorData?.error ?? 'Unknown error');
  }

  const body = (await res.json()) as TokenSecurityApiResponse;
  return 'error' in body ? undefined : body.data.securityInfo;
};

export const solanaTokenSecurityQueryKey = (tokenAddress: string | undefined) =>
  ['solana-token-security', tokenAddress] as const;

export const useSolanaTokenSecurityInfo = ({ tokenAddress }: Params) =>
  useQuery<TokenSecurity | undefined, Error>({
    queryKey: solanaTokenSecurityQueryKey(tokenAddress),
    queryFn: () => fetchTokenSecurity(tokenAddress!),
    enabled: !!tokenAddress,
    refetchOnMount: false,
    staleTime: 60_000,
    retry: 2,
  });
